import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, WS_BASE_URL, settingsAPI } from '../services/api';
import { userLabel, userInitial } from '../utils/displayName';

// Prolific completion redirect for the Guesser group. Sending the participant to
// this URL marks their Prolific submission complete (cc = Prolific completion code).
const GUESSER_PROLIFIC_COMPLETION_URL = 'https://app.prolific.com/submissions/complete?cc=CHB6DQQ5';

// Add CSS for beautiful animations and effects
const injectStyles = () => {
  const styleId = 'home-styles';
  if (document.getElementById(styleId)) return;
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes float {
      0%, 100% {
        transform: translateY(0px);
      }
      50% {
        transform: translateY(-10px);
      }
    }
    
    @keyframes shimmer {
      0% {
        background-position: -1000px 0;
      }
      100% {
        background-position: 1000px 0;
      }
    }
    
    @keyframes gradientShift {
      0%, 100% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
    }
    
    [data-home-card]:hover {
      transform: translateY(-6px) scale(1.02);
      box-shadow: 0 20px 40px rgba(102, 126, 234, 0.25) !important;
    }
    
    [data-home-button]:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4) !important;
    }
    
    [data-home-button]:active:not(:disabled) {
      transform: translateY(0) scale(0.96);
    }
    
    [data-chat-item]:hover {
      background-color: #f9fafb !important;
      transform: translateX(4px);
    }
    
    [data-logout-btn]:hover {
      background-color: rgba(255,255,255,0.3) !important;
      transform: scale(1.05);
    }
  `;
  document.head.appendChild(style);
};

const OnlineUsers = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  useEffect(() => {
    injectStyles();
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [chatRequests, setChatRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]); // Array of {id: requestId, receiver_id: receiverId}
  const [cancellingRequest, setCancellingRequest] = useState(null);
  const [userChatRooms, setUserChatRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingRequest, setSendingRequest] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [toasts, setToasts] = useState([]);
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [unreadByRoom, setUnreadByRoom] = useState(() => {
    try {
      const uid = JSON.parse(localStorage.getItem('user') || '{}')?.id;
      const key = uid ? `unreadByRoom_${uid}` : null;
      if (!key) return {};
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) || {}) : {};
    } catch (e) {
      return {};
    }
  });
  const [wsConnected, setWsConnected] = useState(false);
  const [wsConnecting, setWsConnecting] = useState(false);
  const wsReconnectTimerRef = useRef(null);
  const [settings, setSettings] = useState({});
  const [offlineUserIds, setOfflineUserIds] = useState(() => new Set());
  const [recentOnlineTimestamps, setRecentOnlineTimestamps] = useState({});
  const [presenceLabel, setPresenceLabel] = useState('');
  const presenceTimerRef = useRef(null);
  // Temporarily boost rooms to top on new messages to avoid race with server fetch
  const [boostedUntilByRoomId, setBoostedUntilByRoomId] = useState({});
  const [completionCodeCopied, setCompletionCodeCopied] = useState(false);
  const [showStudyCompletionCode, setShowStudyCompletionCode] = useState(
    () => localStorage.getItem('show_study_completion_code') === 'true'
  );
  // Cultural Turing Test round result (bonus won/lost), captured at chat end.
  const [roundBonus, setRoundBonus] = useState(() => {
    try {
      const raw = localStorage.getItem('round_bonus_result');
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  });
  // The completion code is shown as a one-time popup at chat end. Clear the
  // persisted flag as soon as it triggers so it never lingers on this browser
  // for the next participant who logs into the same (shared) account.
  useEffect(() => {
    if (showStudyCompletionCode) {
      try { localStorage.removeItem('show_study_completion_code'); } catch (_) {}
      try { localStorage.removeItem('round_bonus_result'); } catch (_) {}
    }
  }, [showStudyCompletionCode]);

  // --- Automatic matchmaking ---------------------------------------------
  // The participant no longer picks someone from a list. On this screen we poll
  // POST /chat/match, which atomically pairs them with a currently-free partner
  // of the complementary role. While waiting we show a countdown; if it runs out
  // with no partner, we surface the completion code so they can return to Prolific.
  const [matchTimedOut, setMatchTimedOut] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const matchDoneRef = useRef(false);      // true once matched (or timed out) -> stop polling
  const matchDeadlineRef = useRef(null);   // ms timestamp when the wait window ends

  const onlineIdSet = useMemo(() => new Set((onlineUsers || []).map(u => u.id)), [onlineUsers]);
  
  const { user, logout, isWsOpen, unreadByRoom: unreadFromCtx } = useAuth();
  const navigate = useNavigate();
  const wsRef = useRef(null);

  const fetchOnlineUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/users/online`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      // Show exactly what backend reports as online; avoid client-side filtering that could hide users
      const sorted = (response.data || [])
        .filter(u => u && u.id)
        .slice()
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      setOnlineUsers(sorted);
    } catch (error) {
      console.error('Failed to fetch online users:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChatRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔑 Token:', token ? 'Present' : 'Missing');
      
      const response = await axios.get(`${API_BASE_URL}/chat/requests`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('🔍 Chat requests response:', response.data);
      
      // Update received requests
      const receivedRequests = response.data.received || [];
      console.log('📋 Received requests:', receivedRequests);
      setChatRequests(receivedRequests);
      
      // Show requests modal if there are pending requests
      if (receivedRequests.length > 0) {
        console.log('🔔 Showing requests modal - found', receivedRequests.length, 'requests');
        setShowRequestsModal(true);
      }
      
      // Update sent requests for UI state - store full objects with id and receiver_id
      const sentRequestsData = (response.data.sent || []).filter(req => req.status === 'pending');
      setSentRequests(sentRequestsData.map(req => ({ id: req.id, receiver_id: req.receiver_id })));
      
    } catch (error) {
      console.error('Failed to fetch chat requests:', error);
    }
  }, []);

  const fetchUserChatRooms = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/chat/rooms`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log('🏠 Chat rooms response:', response.data);
      const rooms = (response.data || []).filter(r => Array.isArray(r.members) && r.members.length > 0);
      setUserChatRooms(rooms);
      // Do not aggressively delete unread entries here.
      // They will be cleared when the user opens the room or when a chat ends.
    } catch (error) {
      console.error('Failed to fetch chat rooms:', error);
    }
  }, []);

  const setupWebSocket = useCallback(() => {
    // Sidebar no longer opens its own WebSocket.
    // Presence, chat requests, and message events are handled by the global WS in AuthContext.
    return;
  }, [user, navigate, fetchChatRequests, fetchOnlineUsers, fetchUserChatRooms]);

  // Also listen to a single global WS stream to avoid multiple sockets and flicker
  useEffect(() => {
    const handler = async (e) => {
      const data = e.detail || {};
      try {
        if (data.type === 'ws_open') {
          setWsConnected(true);
          // On socket open, immediately sync sidebar lists so UI is hot on refresh
          try {
            fetchOnlineUsers();
            fetchChatRequests();
            fetchUserChatRooms();
            setTimeout(() => fetchUserChatRooms(), 200);
            // matchmaking removed
          } catch (_) {}
          return;
        }
        if (data.type === 'ws_close') {
          setWsConnected(false);
          return;
        }
        if (data.type === 'user_status_change') {
          // Remove on explicit logout; also remove if backend pruned user (no WS + stale)
          if (data.user_id && data.is_online === false) {
            setOfflineUserIds(prev => new Set(prev).add(data.user_id));
            setOnlineUsers(prev => (Array.isArray(prev) ? prev.filter(u => u.id !== data.user_id) : prev));
          } else if (data.user_id && data.is_online === true) {
            setOfflineUserIds(prev => { const next = new Set(prev); next.delete(data.user_id); return next; });
            setRecentOnlineTimestamps(prev => ({ ...prev, [data.user_id]: Date.now() }));
            // Immediately fetch to add new user to list
            fetchOnlineUsers();
          }
          // Delayed refresh to sync with server (don't call twice if user came online)
          if (data.is_online === false) {
            setTimeout(() => fetchOnlineUsers(), 500);
          }
        } else if (data.type === 'chat_request') {
          // Auto-show incoming requests without manual refresh
          fetchChatRequests();
          setShowRequestsModal(true);
          const name = data.requester?.id ? userLabel(data.requester) : 'someone';
          setNotificationMessage(`🔔 New chat request from ${name}!`);
          setShowNotifications(true);
          setTimeout(() => setShowNotifications(false), 5000);
        } else if (data.type === 'chat_request_accepted') {
          // Refresh rooms so Start Chat becomes available; do not auto-navigate
          fetchUserChatRooms();
          setNotificationMessage('✅ Chat request accepted! Click "Start Chat" to begin.');
          setShowNotifications(true);
          setTimeout(() => setShowNotifications(false), 3000);
        } else if (data.type === 'matched') {
          // Auto-match paired us with a partner -> jump straight into the room,
          // unless we already matched or the wait already timed out.
          if (!matchDoneRef.current && data.room_id) {
            matchDoneRef.current = true;
            navigate(`/chat-room/${data.room_id}`);
          }
        } else if (data.type === 'chat_request_cancelled') {
          // Sender cancelled their request - immediately remove from incoming requests
          const reqId = data.request_id;
          const requesterId = data.requester_id;
          if (reqId) {
            setChatRequests(prev => prev.filter(r => r.id !== reqId));
          } else if (requesterId) {
            setChatRequests(prev => prev.filter(r => r.requester?.id !== requesterId));
          }
          // Also sync with server
          fetchChatRequests();
        } else if (data.type === 'chat_request_rejected') {
          // Receiver declined our request - immediately remove from sent requests
          const reqId = data.request_id;
          if (reqId) {
            setSentRequests(prev => prev.filter(r => r.id !== reqId));
          }
          // Show notification
          setNotificationMessage('😔 Chat request was declined');
          setShowNotifications(true);
          setTimeout(() => setShowNotifications(false), 3000);
          // Also sync with server
          fetchChatRequests();
        } else if (data.type === 'new_message') {
          // Update sidebar instantly: refresh preview for that room and move it to top
          try {
            const roomId = String(data?.data?.room_id || data.room_id || '');
            if (roomId) {
              setUserChatRooms(prev => {
                if (!Array.isArray(prev)) return prev;
                const next = [...prev];
                const idx = next.findIndex(r => String(r.id) === roomId);
                const msg = data?.data || {};
                if (idx > -1) {
                  const room = { ...next[idx] };
                  room.latest_message = {
                    ...(room.latest_message || {}),
                    content: msg.content ?? room.latest_message?.content,
                    original_content: msg.original_content ?? room.latest_message?.original_content,
                    sender: msg.sender ?? room.latest_message?.sender,
                    created_at: msg.created_at ?? room.latest_message?.created_at,
                    target_language: msg.target_language ?? room.latest_message?.target_language,
                  };
                  next.splice(idx, 1);
                  next.unshift(room);
                  // Mark boosted for immediate top placement across any subsequent refresh
                  setBoostedUntilByRoomId(prevBoost => ({ ...prevBoost, [roomId]: Date.now() + 2500 }));
                  return next;
                }
                // If room not present yet, insert a minimal row so user sees it immediately
                try {
                  const meId = user?.id || JSON.parse(localStorage.getItem('user') || '{}')?.id;
                  const sender = msg.sender || {};
                  const memberForRow = (sender && sender.id && sender.id !== meId) ? sender : null;
                  const minimalRoom = {
                    id: Number(roomId),
                    members: memberForRow ? [memberForRow] : [],
                    latest_message: {
                      content: msg.content || '',
                      original_content: msg.original_content || msg.content || '',
                      sender: sender || null,
                      created_at: msg.created_at || new Date().toISOString(),
                      target_language: msg.target_language || 'none',
                    }
                  };
                  // Avoid duplicates
                  if (!next.some(r => String(r.id) === roomId)) {
                    next.unshift(minimalRoom);
                    setBoostedUntilByRoomId(prevBoost => ({ ...prevBoost, [roomId]: Date.now() + 2500 }));
                    return next;
                  }
                } catch (_) {}
                return prev;
              });
            }
          } catch (_) {}
          // Ensure server truth quickly (and account for commit/propagation) without reload
          fetchUserChatRooms();
          setTimeout(() => { fetchUserChatRooms(); fetchOnlineUsers(); }, 150);
          setTimeout(() => { fetchUserChatRooms(); fetchOnlineUsers(); }, 600);
        } else if (data.type === 'chat_ended') {
          try { localStorage.setItem('show_study_completion_code', 'true'); } catch (_) {}
          setShowStudyCompletionCode(true);
          fetchUserChatRooms();
          fetchOnlineUsers();
          // Also clear unread for that room id as it no longer exists
          try {
            const rid = String(data?.room_id || '');
            if (rid) {
              setUnreadByRoom(prev => { const copy = { ...prev }; delete copy[rid]; return copy; });
              const myId = user?.id || JSON.parse(localStorage.getItem('user') || '{}')?.id;
              if (myId) {
                const key = `unreadByRoom_${myId}`;
                const raw = localStorage.getItem(key);
                const map = raw ? (JSON.parse(raw) || {}) : {};
                if (map[rid]) { delete map[rid]; localStorage.setItem(key, JSON.stringify(map)); }
              }
            }
          } catch (_) {}
          // Chat ended: rooms already refreshed above; participant stays on the waiting screen.
        } else if (data.type === 'settings_updated') {
          settingsAPI.get().then(res => setSettings(res.data?.settings || {})).catch(() => {});
        }
      } catch (e) {}
    };
    window.addEventListener('app_ws_event', handler);
    return () => window.removeEventListener('app_ws_event', handler);
  }, [fetchOnlineUsers, fetchUserChatRooms]);

  // Smooth presence label to avoid flicker between Live/Connecting during brief refreshes
  useEffect(() => {
    if (presenceTimerRef.current) {
      clearTimeout(presenceTimerRef.current);
      presenceTimerRef.current = null;
    }
    if (wsConnected) {
      setPresenceLabel('live');
      return;
    }
    if (wsConnecting) {
      presenceTimerRef.current = setTimeout(() => {
        setPresenceLabel('connecting');
      }, 1200);
      return;
    }
    // Offline fallback, delayed a bit to avoid transient blips
    presenceTimerRef.current = setTimeout(() => {
      setPresenceLabel('offline');
    }, 2000);
    return () => {
      if (presenceTimerRef.current) {
        clearTimeout(presenceTimerRef.current);
        presenceTimerRef.current = null;
      }
    };
  }, [wsConnected, wsConnecting]);

  useEffect(() => {
    fetchOnlineUsers();
    fetchChatRequests();
    fetchUserChatRooms();
    // Load settings on mount
    settingsAPI.get().then(res => setSettings(res.data?.settings || {})).catch(() => {});
    // matchmaking removed
    
    // Cross-tab presence sync: listen to storage events
    const onStorage = (e) => {
      try {
        // Presence cross-tab sync
        if (!e || e.key === 'presence_change') {
          fetchOnlineUsers();
          return;
        }
        // Rooms changed signal from global context or other tabs
        if (e && e.key === 'rooms_changed') {
          fetchUserChatRooms();
          return;
        }
        // Unread counts cross-tab sync for current user
        const uid = (user && user.id) || (JSON.parse(localStorage.getItem('user') || '{}')?.id);
        if (!uid) return;
        const expectedKey = `unreadByRoom_${uid}`;
        if (e.key === expectedKey) {
          const raw = localStorage.getItem(expectedKey);
          const map = raw ? (JSON.parse(raw) || {}) : {};
          setUnreadByRoom(map);
        }
      } catch (_) {}
    };
    window.addEventListener('storage', onStorage);

    // Listen for same-tab unread updates emitted by AuthContext so badges update instantly
    const onUnread = (e) => {
      try {
        const uid = (user && user.id) || (JSON.parse(localStorage.getItem('user') || '{}')?.id);
        if (!uid) return;
        const key = `unreadByRoom_${uid}`;
        const raw = localStorage.getItem(key);
        const map = raw ? (JSON.parse(raw) || {}) : {};
        setUnreadByRoom(map);
      } catch (_) {}
    };
    window.addEventListener('unread_updated', onUnread);
    
    // Same-tab rooms_changed event for immediate refresh without relying only on storage
    const onRoomsChanged = () => { fetchUserChatRooms(); };
    window.addEventListener('rooms_changed', onRoomsChanged);

    // Frequent polling for chat requests (every 10 seconds) to ensure they're never missed
    const requestInterval = setInterval(() => {
      fetchChatRequests();
    }, 10000);
    
    // Periodic sync (1 min) as a safety net; realtime events handle most updates
    const interval = setInterval(() => {
      fetchOnlineUsers();
      fetchChatRequests();
      fetchUserChatRooms();
    }, 60000);

    // Poll unread counts from localStorage as a safety net (2s)
    const unreadPollId = setInterval(() => {
      try {
        const uid = (user && user.id) || (JSON.parse(localStorage.getItem('user') || '{}')?.id);
        if (!uid) return;
        const key = `unreadByRoom_${uid}`;
        const raw = localStorage.getItem(key);
        const map = raw ? (JSON.parse(raw) || {}) : {};
        // Only update state if changed
        const prev = unreadByRoom || {};
        const prevKeys = Object.keys(prev).sort().join('|');
        const nextKeys = Object.keys(map).sort().join('|');
        if (prevKeys !== nextKeys) {
          setUnreadByRoom(map);
        } else {
          for (const k of Object.keys(map)) {
            if ((prev[k] || 0) !== (map[k] || 0)) { setUnreadByRoom(map); break; }
          }
        }
      } catch (_) {}
    }, 2000);

    return () => {
      clearInterval(interval);
      clearInterval(unreadPollId);
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
        wsReconnectTimerRef.current = null;
      }
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('unread_updated', onUnread);
      window.removeEventListener('rooms_changed', onRoomsChanged);
    };
  }, [user, setupWebSocket, fetchOnlineUsers, fetchChatRequests, fetchUserChatRooms]);

  // Persist unread counts for current user
  useEffect(() => {
    if (!user) return;
    try {
      const key = `unreadByRoom_${user.id}`;
      localStorage.setItem(key, JSON.stringify(unreadByRoom || {}));
    } catch (e) {
      // ignore storage errors
    }
  }, [unreadByRoom, user]);

  // Hydrate unread map immediately from localStorage on mount/user change so bolding persists after refresh
  useEffect(() => {
    try {
      const uid = (user && user.id) || (JSON.parse(localStorage.getItem('user') || '{}')?.id);
      if (!uid) return;
      const key = `unreadByRoom_${uid}`;
      const raw = localStorage.getItem(key);
      const map = raw ? (JSON.parse(raw) || {}) : {};
      setUnreadByRoom(map);
    } catch (_) {}
  }, [user]);

  // Clear unread for the last seen room if present, to avoid sticky bold after returns
  useEffect(() => {
    // Clear unread for the last seen room if present, to avoid sticky bold after returns
    try {
      const last = localStorage.getItem('lastSeenRoomId');
      const uid = (user && user.id) || (JSON.parse(localStorage.getItem('user') || '{}')?.id);
      if (last && uid) {
        const key = `unreadByRoom_${uid}`;
        const raw = localStorage.getItem(key);
        const map = raw ? (JSON.parse(raw) || {}) : {};
        if (map[String(last)]) {
          delete map[String(last)];
          localStorage.setItem(key, JSON.stringify(map));
          setUnreadByRoom(map);
          try {
            const total = Object.values(map).reduce((a,b)=>a+(b||0),0);
            const ev = new CustomEvent('unread_updated', { detail: { roomId: String(last), total } });
            window.dispatchEvent(ev);
          } catch (_) {}
        }
        localStorage.removeItem('lastSeenRoomId');
      }
    } catch (_) {}
  }, [user]);

  // Apply app.title to browser tab
  useEffect(() => {
    const newTitle = settings['app.title'] || 'Research Chat App';
    if (document && document.title !== newTitle) {
      document.title = newTitle;
    }
  }, [settings]);

  // Heartbeat to keep online status accurate while the tab is open
  useEffect(() => {
    if (!user) return;
    let hbId;
    const sendHeartbeat = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        await axios.post(`${API_BASE_URL}/auth/heartbeat`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        // ignore
      }
    };
    sendHeartbeat();
    hbId = setInterval(sendHeartbeat, 3000);
    // Refresh presence more often
    const presenceId = setInterval(() => {
      fetchOnlineUsers();
    }, 4000);
    const onFocus = () => { sendHeartbeat(); fetchOnlineUsers(); };
    window.addEventListener('focus', onFocus);
    return () => {
      if (hbId) clearInterval(hbId);
      if (presenceId) clearInterval(presenceId);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, fetchOnlineUsers]);

  // Auto-match poll + wait countdown. Stops once matched, timed out, or a chat
  // just ended (completion popup showing) so we never re-match a finished user.
  useEffect(() => {
    if (matchDoneRef.current || matchTimedOut || showStudyCompletionCode) return;

    // Establish the wait deadline once (default 5 minutes; admin-overridable).
    if (matchDeadlineRef.current == null) {
      const secs = parseInt(settings['match.timeout.seconds'], 10);
      const total = Number.isFinite(secs) && secs > 0 ? secs : 300;
      matchDeadlineRef.current = Date.now() + total * 1000;
      setSecondsLeft(total);
    }

    let stopped = false;
    const goToRoom = (roomId) => {
      if (matchDoneRef.current) return;
      matchDoneRef.current = true;
      stopped = true;
      navigate(`/chat-room/${roomId}`);
    };

    const tryMatch = async () => {
      if (stopped || matchDoneRef.current) return;
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API_BASE_URL}/chat/match`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data && res.data.matched && res.data.room_id) {
          goToRoom(res.data.room_id);
        }
      } catch (_) {}
    };

    tryMatch();
    const pollId = setInterval(tryMatch, 2500);
    const tickId = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((matchDeadlineRef.current - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        matchDoneRef.current = true; // block any late "matched" WS nav after timeout
        setMatchTimedOut(true);
      }
    }, 1000);

    return () => { stopped = true; clearInterval(pollId); clearInterval(tickId); };
  }, [matchTimedOut, showStudyCompletionCode, settings, navigate]);

  const sendChatRequest = async (receiverId) => {
    setSendingRequest(receiverId);
    
    // Optimistic update - immediately show as pending
    const tempId = Date.now();
    setSentRequests(prev => [...prev, { id: tempId, receiver_id: receiverId }]);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/chat/request`, 
        {
          receiver_id: receiverId,
          message: `Hi! I'd like to chat with you.`
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Update with real ID from server
      setSentRequests(prev => prev.map(req => 
        req.id === tempId ? { id: response.data.id, receiver_id: receiverId } : req
      ));
      
      // Show success notification
      setNotificationMessage('✨ Chat request sent successfully!');
      setShowNotifications(true);
      setTimeout(() => setShowNotifications(false), 3000);
      
    } catch (error) {
      console.error('Failed to send chat request:', error);
      // Revert optimistic update on error
      setSentRequests(prev => prev.filter(req => req.id !== tempId));
      
      if (error.response?.status === 400) {
        // Refresh to get actual state
        fetchChatRequests();
        setNotificationMessage(error.response.data?.detail || '⏳ Request already pending');
        setShowNotifications(true);
        setTimeout(() => setShowNotifications(false), 2500);
      } else {
        setNotificationMessage('⚠️ Could not send request');
        setShowNotifications(true);
        setTimeout(() => setShowNotifications(false), 2500);
      }
    } finally {
      setSendingRequest(null);
    }
  };

  const cancelChatRequest = async (requestId, receiverId) => {
    setCancellingRequest(receiverId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/chat/request/${requestId}/cancel`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Remove from sent requests
      setSentRequests(prev => prev.filter(req => req.id !== requestId));
      
      // Show success notification
      setNotificationMessage('✅ Chat request cancelled');
      setShowNotifications(true);
      setTimeout(() => setShowNotifications(false), 2500);
      
    } catch (error) {
      console.error('Failed to cancel chat request:', error);
      setNotificationMessage(error.response?.data?.detail || '⚠️ Failed to cancel request');
      setShowNotifications(true);
      setTimeout(() => setShowNotifications(false), 2500);
    } finally {
      setCancellingRequest(null);
    }
  };

  const respondToChatRequest = async (requestId, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/chat/request/${requestId}/respond`, 
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Chat request response:', response.data);
      
      if (status === 'accepted') {
        // Just show success message, don't auto-navigate
        console.log('Chat request accepted successfully');
        setNotificationMessage('✅ Chat request accepted! Click "Start Chat" to begin.');
        setShowNotifications(true);
        setTimeout(() => setShowNotifications(false), 3000);
        // Refresh user rooms so the Start Chat button appears immediately
        await fetchUserChatRooms();
      }
      
      fetchChatRequests(); // Refresh requests
      // fetchUserChatRooms(); // already refreshed above for accepted case
      
      // Hide modal if no more requests
      setTimeout(() => {
        if (chatRequests.length <= 1) {
          setShowRequestsModal(false);
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to respond to chat request:', error);
      console.error('Error details:', error.response?.data);
      // Show a gentle toast instead of blocking alert
      setNotificationMessage(error.response?.data?.detail || 'Failed to respond to request. Please try again.');
      setShowNotifications(true);
      setTimeout(() => setShowNotifications(false), 2500);
    }
  };

  const getLanguageName = (code) => {
    const languages = {
      'en': 'English', 'es': 'Spanish', 'fr': 'French', 'de': 'German',
      'it': 'Italian', 'pt': 'Portuguese', 'ru': 'Russian', 'zh': 'Chinese',
      'ja': 'Japanese', 'ko': 'Korean', 'ar': 'Arabic', 'hi': 'Hindi', 'bn': 'Bengali'
    };
    return languages[code] || code;
  };

  const findChatRoomWithUser = (otherUserId) => {
    console.log('🔍 Finding chat room with user:', otherUserId);
    console.log('🏠 Available rooms:', userChatRooms);
    const foundRoom = userChatRooms.find(room => 
      room.members.some(member => member.id === otherUserId)
    );
    console.log('✅ Found room:', foundRoom);
    return foundRoom;
  };

  const totalUnread = Object.values(unreadByRoom).reduce((a, b) => a + (b || 0), 0);

  // Keep local unread state in sync with AuthContext (single source of truth)
  useEffect(() => {
    try {
      if (unreadFromCtx && typeof unreadFromCtx === 'object') {
        const hasAny = Object.keys(unreadFromCtx || {}).length > 0;
        if (hasAny) {
          setUnreadByRoom(unreadFromCtx);
        }
      }
    } catch (_) {}
  }, [unreadFromCtx]);
  const chatsRef = useRef(null);
  const scrollToChats = () => {
    chatsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingCard}>
          <div style={styles.spinner}></div>
          <h3 style={styles.loadingText}>Loading online users...</h3>
        </div>
      </div>
    );
  }

  const renderWaitingScreen = () => {
    const totalSecs = (() => {
      const s = parseInt(settings['match.timeout.seconds'], 10);
      return Number.isFinite(s) && s > 0 ? s : 300;
    })();
    const shown = secondsLeft == null ? totalSecs : secondsLeft;
    const mm = Math.floor(shown / 60);
    const ss = String(shown % 60).padStart(2, '0');
    const code = settings['study.completion_code'];
    const isGuesser = user?.user_type === 'guesser';

    if (matchTimedOut) {
      return (
        <div style={{ textAlign: 'center', padding: isMobile ? '24px 8px 8px' : '32px 8px 8px' }}>
          <div style={{ fontSize: isMobile ? 44 : 56, marginBottom: 12 }}>⏳</div>
          <h2 style={{ margin: '0 0 10px', fontSize: isMobile ? 22 : 28, fontWeight: 800, color: '#111827' }}>
            {settings['match.timeout.title'] || 'No partner available right now'}
          </h2>
          <p style={{ margin: '0 auto 24px', maxWidth: 520, fontSize: isMobile ? 15 : 17, lineHeight: 1.6, color: '#4b5563' }}>
            {settings['match.timeout.text'] ||
              "We couldn't connect you with a partner in time. Thank you for waiting — you can now return to Prolific using the completion code below to receive your reward."}
          </p>
          {code && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '14px 18px', borderRadius: 12, marginBottom: 20,
              background: '#f9fafb', border: '1px dashed rgba(99,102,241,0.4)',
            }}>
              <span style={{
                fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
                fontSize: isMobile ? 20 : 24, fontWeight: 700, letterSpacing: '0.12em',
                color: '#111827', userSelect: 'all',
              }}>{code}</span>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(code);
                    setCompletionCodeCopied(true);
                    setTimeout(() => setCompletionCodeCopied(false), 2200);
                  } catch (e) { console.warn('Copy failed', e); }
                }}
                style={{
                  padding: '9px 16px', fontSize: 13, fontWeight: 600, border: 'none',
                  borderRadius: 10, cursor: 'pointer', color: 'white',
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                }}
              >
                {completionCodeCopied ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}
          {isGuesser && (
            <div style={{ marginTop: 4 }}>
              <a
                href={GUESSER_PROLIFIC_COMPLETION_URL}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '13px 24px', fontSize: 16, fontWeight: 700, textDecoration: 'none',
                  borderRadius: 12, color: 'white',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 16px rgba(5,150,105,0.35)',
                }}
              >
                Complete study on Prolific →
              </a>
            </div>
          )}
        </div>
      );
    }

    return (
      <div style={{ textAlign: 'center', padding: isMobile ? '28px 8px 12px' : '44px 8px 20px' }}>
        <div style={{ ...styles.spinner, margin: '0 auto 24px' }} />
        <h2 style={{ margin: '0 0 10px', fontSize: isMobile ? 22 : 28, fontWeight: 800, color: '#111827' }}>
          {settings['match.wait.title'] || 'Looking for a partner…'}
        </h2>
        <p style={{ margin: '0 auto 28px', maxWidth: 520, fontSize: isMobile ? 15 : 17, lineHeight: 1.6, color: '#4b5563' }}>
          {settings['match.wait.text'] ||
            "No one is available to connect with right now. Please keep this page open — we'll connect you automatically as soon as another participant is ready."}
        </p>
        <div style={{
          display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
          padding: isMobile ? '16px 28px' : '20px 40px', borderRadius: 16,
          background: '#eef2ff', border: '1px solid #c7d2fe',
        }}>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: '#6366f1', textTransform: 'uppercase' }}>
            Time remaining
          </span>
          <span style={{
            fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
            fontSize: isMobile ? 40 : 52, fontWeight: 800, color: '#4338ca', lineHeight: 1.1,
          }}>
            {mm}:{ss}
          </span>
        </div>
        <p style={{ margin: '20px auto 0', fontSize: 13, color: '#9ca3af' }}>
          Please don't close or refresh this page.
        </p>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Notifications disabled per request */}
      
      {/* Floating Chat Requests Indicator */}
      {chatRequests.length > 0 && (
        <div style={styles.floatingIndicator} onClick={() => setShowRequestsModal(!showRequestsModal)}>
          <span style={styles.indicatorIcon}>💬</span>
          <span style={styles.indicatorBadge}>{chatRequests.length}</span>
        </div>
      )}

      {/* Floating Inbox removed as requested */}

      {/* Mobile Chat Toggle Button */}
      {isMobile && (
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          style={{
            position: 'fixed',
            bottom: 20,
            left: 20,
            padding: showMobileMenu ? '14px' : '14px 20px',
            borderRadius: showMobileMenu ? '50%' : 28,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            boxShadow: '0 6px 24px rgba(102, 126, 234, 0.5)',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
          }}
        >
          {showMobileMenu ? '✕' : '💬'}
          {!showMobileMenu && <span>My Chats</span>}
          {!showMobileMenu && userChatRooms.length > 0 && (
            <span style={{
              backgroundColor: '#ef4444',
              color: 'white',
              borderRadius: '50%',
              width: 22,
              height: 22,
              fontSize: 12,
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: 4
            }}>
              {userChatRooms.length}
            </span>
          )}
        </button>
      )}

      {/* WhatsApp-style chat sidebar */}
      <div
        style={{
          position: 'fixed', 
          top: isMobile ? 0 : 120, 
          left: isMobile ? (showMobileMenu ? 0 : '-100%') : 20, 
          width: isMobile ? '85%' : 340, 
          maxWidth: isMobile ? '320px' : 340,
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '75vh',
          backgroundColor: 'white', border: '2px solid transparent',
          backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          borderRadius: isMobile ? 0 : 20,
          boxShadow: '0 20px 40px rgba(102, 126, 234, 0.2)', overflow: 'hidden', zIndex: isMobile ? 1000 : 900,
          animation: 'fadeInUp 0.6s ease-out',
          transition: 'left 0.3s ease'
        }}
      >
        <div style={{
          padding: '16px 20px', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '2px solid rgba(255,255,255,0.1)'
        }}>
          <div style={{ fontWeight: 700, color: 'white', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>💬</span>
            <span>Chats</span>
          </div>
          {isMobile && (
            <button
              onClick={() => setShowMobileMenu(false)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                width: 32,
                height: 32,
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          )}
        </div>
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {userChatRooms.length === 0 ? (
            <div style={{ padding: 12, color: '#6b7280' }}>No chats yet</div>
          ) : (
            [...userChatRooms]
              .sort((a, b) => {
                const at = a.latest_message?.created_at ? new Date(a.latest_message.created_at).getTime() : 0;
                const bt = b.latest_message?.created_at ? new Date(b.latest_message.created_at).getTime() : 0;
                return bt - at;
              })
              .map(room => {
                const member = room.members?.[0];
                const unread = (() => {
                  try {
                    const uid = (user && user.id) || (JSON.parse(localStorage.getItem('user') || '{}')?.id);
                    if (!uid) return unreadByRoom[String(room.id)] || 0;
                    const key = `unreadByRoom_${uid}`;
                    const raw = localStorage.getItem(key);
                    const map = raw ? (JSON.parse(raw) || {}) : {};
                    return map[String(room.id)] || 0;
                  } catch (_) {
                    return unreadByRoom[String(room.id)] || 0;
                  }
                })();
                const isMine = room.latest_message?.sender?.id === user?.id;
                const preview = (() => {
                  const msg = room.latest_message;
                  if (!msg) return '';
                  // No translation: always show the raw message.
                  return msg.original_content || msg.content || '';
                })();
                return (
                  <div
                    key={room.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                      backgroundColor: 'white',
                      transition: 'all 0.3s ease'
                    }}
                    data-chat-item
                    onClick={() => {
                      setUnreadByRoom(prev => { const copy = { ...prev }; delete copy[String(room.id)]; return copy; });
                      try {
                        const uid = (user && user.id) || (JSON.parse(localStorage.getItem('user') || '{}')?.id);
                        if (uid) {
                          const key = `unreadByRoom_${uid}`;
                          const raw = localStorage.getItem(key);
                          const map = raw ? (JSON.parse(raw) || {}) : {};
                          if (map[String(room.id)]) {
                            delete map[String(room.id)];
                            localStorage.setItem(key, JSON.stringify(map));
                            try {
                              const total = Object.values(map).reduce((a,b)=>a+(b||0),0);
                              const ev = new CustomEvent('unread_updated', { detail: { roomId: String(room.id), total } });
                              window.dispatchEvent(ev);
                            } catch (_) {}
                          }
                        }
                      } catch (e) {
                        // ignore storage errors
                      }
                      navigate(`/chat-room/${room.id}`);
                    }}
                  >
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                      position: 'relative',
                      fontSize: '18px',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)'
                    }}>
                      {member?.id ? userInitial(member) : '?'}
                      {member?.id && onlineIdSet.has(member.id) && !offlineUserIds.has(member.id) && (
                        <span style={{
                          position: 'absolute', bottom: 0, right: 0, width: 14, height: 14,
                          backgroundColor: '#10b981', border: '3px solid white', borderRadius: '50%',
                          boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.3)',
                          animation: 'pulse-ring 2s ease-out infinite'
                        }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: '#111827', fontSize: '15px' }}>
                        {member?.id ? userLabel(member) : 'Private Chat'}
                      </div>
                      {preview && (
                        <div style={{ fontSize: 13, color: '#4b5563', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 400 }}>
                          {isMine ? 'You: ' : ''}{preview}
                        </div>
                      )}
                    </div>
                    {/* No numeric badge or highlighting */}
                  </div>
                );
              })
          )}
        </div>
      </div>

      {/* Mobile overlay backdrop */}
      {isMobile && showMobileMenu && (
        <div 
          onClick={() => setShowMobileMenu(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 999
          }}
        />
      )}

      <div style={{ 
        ...styles.card, 
        marginLeft: isMobile ? 0 : 380,
        borderRadius: isMobile ? 16 : 24
      }}>
        {/* Beautiful Header */}
        {isMobile ? (
          /* Mobile Header - Clean compact design */
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '16px',
            paddingTop: 'max(16px, env(safe-area-inset-top, 16px))',
            color: 'white'
          }}>
            {/* Top row: User info + Sign out */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  fontWeight: 'bold',
                  border: '2px solid rgba(255,255,255,0.3)'
                }}>
                  {user?.id ? userInitial(user) : ''}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{userLabel(user)}</div>
                  <div style={{ fontSize: 11, opacity: 0.85, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {isWsOpen && (
                      <span style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: '#4ade80',
                        marginLeft: 4
                      }} />
                    )}
                  </div>
                </div>
              </div>
              <button 
                onClick={logout} 
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 14px',
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                🚪 Sign Out
              </button>
            </div>
            
            {/* Title section */}
            <div>
              <h1 style={{
                fontSize: 22,
                fontWeight: 800,
                margin: '0 0 6px 0',
                background: 'linear-gradient(45deg, #ffffff, #f0f9ff)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent'
              }}>🌍 {settings['home.title'] || 'Global Chat Hub'}</h1>
              <p style={{
                fontSize: 12,
                margin: 0,
                opacity: 0.9
              }}>{settings['home.subtitle'] || 'A place to connect • Chat in real time'}</p>
            </div>
          </div>
        ) : (
          /* Desktop Header */
          <div style={styles.header}>
            {/* Decorative elements */}
            <div style={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
              pointerEvents: 'none',
              animation: 'float 6s ease-in-out infinite'
            }} />
            <div style={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 150,
              height: 150,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
              pointerEvents: 'none',
              animation: 'float 8s ease-in-out infinite reverse'
            }} />
            <div style={styles.headerContent}>
              <div style={styles.headerLeft}>
                <div style={styles.titleSection}>
                  <h1 style={styles.title}>🌍 {settings['home.title'] || 'Global Chat Hub'}</h1>
                  <p style={styles.subtitle}>{settings['home.subtitle'] || 'A place to connect • Chat in real time'}</p>
                </div>
              </div>
              
              <div style={styles.headerRight}>
                <div style={styles.userProfile}>
                  <div style={styles.userAvatar}>
                    {user?.id ? userInitial(user) : ''}
                  </div>
                  <div style={styles.userDetails}>
                    <span style={styles.userName}>{userLabel(user)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isWsOpen && (
                      <span
                        aria-label="online"
                        title="Online"
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: '#10b981',
                          border: '2px solid rgba(255,255,255,0.9)'
                        }}
                      />
                    )}
                  </div>
                  <button onClick={logout} style={styles.logoutBtn} data-logout-btn>
                    <span style={{ marginRight: '6px' }}>🚪</span>
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showStudyCompletionCode && (
          <div
            role="dialog"
            aria-modal="true"
            onClick={user?.user_type === 'guesser' ? undefined : () => setShowStudyCompletionCode(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 2000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(17, 24, 39, 0.55)', padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 460, background: 'white',
                borderRadius: 18, padding: isMobile ? '24px 20px' : '32px 28px',
                boxShadow: '0 24px 60px rgba(0,0,0,0.3)', textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
              <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: '#111827' }}>
                Chat complete
              </h2>
              {roundBonus && (() => {
                const cents = Number.isFinite(roundBonus.cents) ? roundBonus.cents : null;
                let won = false;
                let title = '';
                let sub = '';
                if (roundBonus.role === 'guesser') {
                  if (roundBonus.correct === true) {
                    won = true;
                    title = `✅ Correct! You earned a ${cents ?? 0}¢ bonus.`;
                    sub = 'The faster you guessed, the more you kept.';
                  } else if (roundBonus.correct === false) {
                    title = '❌ Wrong guess — no bonus this round.';
                    sub = 'A wrong final guess forfeits the entire bonus.';
                  } else {
                    title = 'Your verdict has been recorded.';
                  }
                } else if (roundBonus.role === 'pretender') {
                  if (roundBonus.believed === true) {
                    won = true;
                    title = `🎭 You convinced them! You earned a ${cents ?? 0}¢ bonus.`;
                    sub = 'They believed you were German.';
                  } else if (roundBonus.believed === false) {
                    title = 'They didn’t believe you — no bonus this round.';
                  } else {
                    title = 'This round has been recorded.';
                  }
                }
                if (!title) return null;
                return (
                  <div style={{
                    margin: '0 0 16px', padding: '14px 16px', borderRadius: 12,
                    background: won ? '#ecfdf5' : '#fef2f2',
                    border: `1px solid ${won ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.35)'}`,
                    textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: won ? '#065f46' : '#991b1b' }}>
                      {title}
                    </div>
                    {sub && (
                      <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                        {sub}
                      </div>
                    )}
                  </div>
                );
              })()}
              {settings['study.completion_code'] && (
                <>
                  <p style={{ margin: '0 0 16px', fontSize: 14, color: '#4b5563', lineHeight: 1.5 }}>
                    Use this completion code when your study asks for it (e.g. on Prolific). Keep it private.
                  </p>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    padding: '12px 16px', borderRadius: 12, marginBottom: 20,
                    background: '#f9fafb', border: '1px dashed rgba(99,102,241,0.4)',
                  }}>
                    <span style={{
                      fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
                      fontSize: isMobile ? 18 : 22, fontWeight: 700, letterSpacing: '0.12em',
                      color: '#111827', userSelect: 'all',
                    }}>
                      {settings['study.completion_code']}
                    </span>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(settings['study.completion_code']);
                          setCompletionCodeCopied(true);
                          setTimeout(() => setCompletionCodeCopied(false), 2200);
                        } catch (e) { console.warn('Copy failed', e); }
                      }}
                      style={{
                        padding: '8px 14px', fontSize: 12, fontWeight: 600, border: 'none',
                        borderRadius: 10, cursor: 'pointer', color: 'white',
                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                      }}
                    >
                      {completionCodeCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </>
              )}
              {user?.user_type === 'guesser' && (
                <div style={{ marginBottom: 20 }}>
                  <a
                    href={GUESSER_PROLIFIC_COMPLETION_URL}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '13px 24px', fontSize: 16, fontWeight: 700, textDecoration: 'none',
                      borderRadius: 12, color: 'white',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      boxShadow: '0 4px 16px rgba(5,150,105,0.35)',
                    }}
                  >
                    Complete study on Prolific →
                  </a>
                </div>
              )}
              {/* Guessers (Prolific participants) must finish via "Complete study on Prolific";
                  only non-guesser internal accounts get a Close button. */}
              {user?.user_type !== 'guesser' && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowStudyCompletionCode(false)}
                    style={{
                      marginTop: 4, padding: '10px 20px', fontSize: 14, fontWeight: 600,
                      border: '1px solid #e5e7eb', borderRadius: 10, background: 'white',
                      color: '#374151', cursor: 'pointer',
                    }}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Suggested Match removed */}

        {/* Chat Requests Modal/Section */}
        {chatRequests.length > 0 && (
          <div style={{
            ...styles.requestsModal,
            margin: isMobile ? '10px' : '20px',
            borderRadius: isMobile ? '12px' : '16px'
          }}>
            <div style={{
              ...styles.requestsHeader,
              padding: isMobile ? '14px 16px' : '20px',
              flexWrap: 'wrap',
              gap: isMobile ? '8px' : '12px'
            }}>
              <h3 style={{
                ...styles.requestsTitle,
                fontSize: isMobile ? '16px' : '20px'
              }}>🔔 Incoming Chat Requests</h3>
              <span style={{
                ...styles.requestsCount,
                fontSize: isMobile ? '12px' : '14px'
              }}>{chatRequests.length} pending</span>
              <button 
                onClick={() => setShowRequestsModal(!showRequestsModal)}
                style={styles.toggleRequestsBtn}
              >
                {showRequestsModal ? '▼' : '▲'}
              </button>
            </div>
            
            {showRequestsModal && (
            
            <div style={{
              ...styles.requestsList,
              padding: isMobile ? '12px' : '20px'
            }}>
              {chatRequests.map(request => {
                return (
                <div key={request.id} style={{
                  ...styles.requestItem,
                  padding: isMobile ? '14px' : '20px',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center'
                }}>
                  <div style={{
                    ...styles.requestLeft,
                    marginBottom: isMobile ? '12px' : 0
                  }}>
                    <div style={{
                      ...styles.requestAvatar,
                      width: isMobile ? '40px' : '50px',
                      height: isMobile ? '40px' : '50px',
                      fontSize: isMobile ? '16px' : '20px'
                    }}>
                      {userInitial(request.requester)}
                    </div>
                    <div style={styles.requestInfo}>
                      <h4 style={styles.requestName}>{userLabel(request.requester)}</h4>
                      {request.message && (
                        <p style={styles.requestMessage}>💭 "{request.message}"</p>
                      )}
                    </div>
                  </div>
                  
                  <div style={{
                    ...styles.requestActions,
                    width: isMobile ? '100%' : 'auto',
                    justifyContent: isMobile ? 'stretch' : 'flex-end'
                  }}>
                    <button 
                      onClick={() => respondToChatRequest(request.id, 'accepted')}
                      style={{
                        ...styles.acceptBtn,
                        flex: isMobile ? 1 : 'none',
                        padding: isMobile ? '10px 14px' : '10px 20px',
                        fontSize: isMobile ? '13px' : '14px'
                      }}
                    >
                      ✨ Accept
                    </button>
                    <button 
                      onClick={() => respondToChatRequest(request.id, 'rejected')}
                      style={{
                        ...styles.declineBtn,
                        flex: isMobile ? 1 : 'none',
                        padding: isMobile ? '10px 14px' : '10px 20px',
                        fontSize: isMobile ? '13px' : '14px'
                      }}
                    >
                      ❌ Decline
                    </button>
                  </div>
                </div>
              );
              })}
            </div>
            )}
          </div>
        )}

        {/* Online Users Section */}
        <div style={{
          ...styles.usersSection,
          padding: isMobile ? '20px 16px' : '48px'
        }}>
          {renderWaitingScreen()}
          {false && (<>
          <div style={styles.sectionHeader}>
            <h2 style={{
              ...styles.sectionTitle,
              fontSize: isMobile ? '22px' : '32px'
            }}>
              <span style={styles.onlineIndicator}>🟢</span>
              People Online ({onlineUsers?.length || 0})
            </h2>
            <p style={{
              ...styles.sectionSubtitle,
              fontSize: isMobile ? '14px' : '17px'
            }}>{settings['home.online.subtitle'] || 'Others taking part may be online now'}</p>


          </div>

          <div style={{
            margin: isMobile ? '0 0 16px' : '0 0 20px',
            padding: isMobile ? '12px 14px' : '14px 18px',
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderRadius: 12,
            color: '#065f46',
            fontSize: isMobile ? '14px' : '15px',
            lineHeight: 1.55
          }}>
            {(settings['home.instructions'] ||
              '👋 Please wait on this page until another participant appears below. When someone becomes available, click "✨ Connect" on their card. Once they accept, a "💬 Chat" button appears — click it to start your conversation.'
            ).split('\n').map((line, idx) => (
              <span key={idx}>{line}<br /></span>
            ))}
          </div>

          {onlineUsers.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon} data-home-card>🌍</div>
              <h3 style={styles.emptyTitle}>{settings['home.empty.title'] || 'Waiting for connections...'}</h3>
              <p style={styles.emptyText}>
                {(settings['home.empty.text'] || 'Other research participants will appear here when they join.').split('\n').map((line, idx) => (
                  <span key={idx}>
                    {line}
                    <br />
                  </span>
                ))}
              </p>
            </div>
          ) : (
            <div style={{
              ...styles.usersGrid,
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: isMobile ? '12px' : '20px'
            }}>
              {onlineUsers
                .map(onlineUser => {
                const isSelf = onlineUser.id === user?.id;
                const sentRequest = sentRequests.find(req => req.receiver_id === onlineUser.id);
                const isPending = !!sentRequest;
                const isRequesting = sendingRequest === onlineUser.id;
                const isCancelling = cancellingRequest === onlineUser.id;
                const existingRoom = findChatRoomWithUser(onlineUser.id);
                // Check if this user has sent a request to current user
                const hasIncomingRequest = chatRequests.some(req => req.requester?.id === onlineUser.id);
                
                return (
                  <div key={onlineUser.id} style={styles.userCard} data-home-card>
                    <div style={styles.cardContent}>
                      <div style={styles.cardTop}>
                        <div style={styles.userInitial}>
                          {userInitial(onlineUser)}
                          <div style={styles.onlineStatus}></div>
                        </div>
                        <div style={styles.userInfo}>
                          <h3 style={styles.cardName}>{userLabel(onlineUser)}{isSelf ? ' (You)' : ''}</h3>
                        </div>
                      </div>
                      
                      <div style={styles.cardActions}>
                        {existingRoom ? (
                          <button
                            onClick={() => {
                              setUnreadByRoom(prev => {
                                const copy = { ...prev };
                                delete copy[String(existingRoom.id)];
                                return copy;
                              });
                              navigate(`/chat-room/${existingRoom.id}`);
                            }}
                            style={styles.chatBtn}
                            data-home-button
                          >
                            💬 Chat
                          </button>
                        ) : isPending ? (
                          <button 
                            onClick={() => cancelChatRequest(sentRequest.id, onlineUser.id)}
                            disabled={isCancelling}
                            style={{
                              ...styles.pendingBtn,
                              cursor: isCancelling ? 'not-allowed' : 'pointer',
                              opacity: isCancelling ? 0.7 : 1
                            }}
                          >
                            {isCancelling ? '⏳ Cancelling...' : '❌ Cancel Request'}
                          </button>
                        ) : hasIncomingRequest ? (
                          <button
                            onClick={() => setShowRequestsModal(true)}
                            style={{
                              ...styles.connectBtn,
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              boxShadow: '0 4px 14px rgba(245, 158, 11, 0.3)'
                            }}
                            data-home-button
                          >
                            📨 View Request
                          </button>
                        ) : (
                          <button
                            onClick={() => sendChatRequest(onlineUser.id)}
                            disabled={isRequesting}
                            style={{
                              ...styles.connectBtn,
                              ...(isRequesting ? styles.connectBtnDisabled : {})
                            }}
                            data-home-button
                          >
                            {isRequesting ? '⏳' : '✨ Connect'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </>)}
        </div>

        {/* Your Chats Section removed as requested */}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundSize: '200% 200%',
    animation: 'gradientShift 15s ease infinite',
    padding: '20px',
    position: 'relative',
  },
  notification: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    backgroundColor: '#10b981',
    color: 'white',
    padding: '15px 20px',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    zIndex: 1000,
    animation: 'slideIn 0.3s ease-out',
  },
  floatingIndicator: {
    position: 'fixed',
    top: '20px',
    left: '20px',
    backgroundColor: '#ef4444',
    color: 'white',
    borderRadius: '25px',
    padding: '12px 16px',
    cursor: 'pointer',
    boxShadow: '0 8px 25px rgba(239,68,68,0.3)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  indicatorIcon: {
    fontSize: '18px',
  },
  indicatorBadge: {
    backgroundColor: 'white',
    color: '#ef4444',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '24px',
    boxShadow: '0 25px 50px rgba(102, 126, 234, 0.25)',
    maxWidth: '1400px',
    margin: '0 auto',
    overflow: 'hidden',
    animation: 'fadeInUp 0.6s ease-out',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
  },
  loadingCard: {
    backgroundColor: 'white',
    borderRadius: '24px',
    padding: '60px',
    textAlign: 'center',
    boxShadow: '0 25px 50px rgba(102, 126, 234, 0.25)',
    maxWidth: '400px',
    margin: '0 auto',
    marginTop: '100px',
    animation: 'fadeInUp 0.6s ease-out',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundSize: '200% 200%',
    animation: 'gradientShift 10s ease infinite',
    color: 'white',
    padding: '36px 40px',
    position: 'relative',
    overflow: 'hidden',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  headerLeft: {
    flex: 1,
  },
  titleSection: {
    marginBottom: '0',
  },
  title: {
    fontSize: '40px',
    fontWeight: '800',
    margin: '0 0 10px 0',
    background: 'linear-gradient(45deg, #ffffff, #f0f9ff, #ffffff)',
    backgroundSize: '200% auto',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    animation: 'shimmer 3s linear infinite',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: '17px',
    opacity: 0.95,
    margin: 0,
    fontWeight: '500',
    textShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
  },
  userProfile: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: 'rgba(255,255,255,0.15)',
    padding: '14px 24px',
    borderRadius: '50px',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.2)',
    flexWrap: 'wrap',
  },
  userAvatar: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.25)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
    border: '3px solid rgba(255,255,255,0.4)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  userName: {
    fontWeight: '600',
    fontSize: '16px',
  },
  userMeta: {
    fontSize: '14px',
    opacity: 0.8,
  },
  logoutBtn: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: 'white',
    border: '2px solid rgba(255,255,255,0.3)',
    padding: '10px 20px',
    borderRadius: '24px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 600,
    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
  },
  requestsModal: {
    backgroundColor: '#fef7cd',
    border: '2px solid #f59e0b',
    margin: '20px',
    borderRadius: '16px',
    overflow: 'hidden',
  },
  requestsHeader: {
    backgroundColor: '#f59e0b',
    color: 'white',
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  requestsTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
  },
  requestsCount: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
  },
  toggleRequestsBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.8)',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
  },
  requestsList: {
    padding: '20px',
  },
  requestItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '15px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    flexWrap: 'wrap',
    gap: '12px',
  },
  requestLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    flex: 1,
  },
  requestAvatar: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    backgroundColor: '#667eea',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  requestInfo: {
    flex: 1,
  },
  requestName: {
    margin: '0 0 5px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937',
  },
  requestDetails: {
    margin: '0 0 8px 0',
    display: 'flex',
    gap: '15px',
  },
  requestFlag: {
    fontSize: '14px',
    color: '#667eea',
    fontWeight: '500',
  },
  requestAge: {
    fontSize: '14px',
    color: '#6b7280',
  },
  requestMessage: {
    margin: '8px 0 0 0',
    fontSize: '14px',
    color: '#374151',
    fontStyle: 'italic',
  },
  requestActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  acceptBtn: {
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
  },
  declineBtn: {
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
  },
  usersSection: {
    padding: '48px',
    background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(102, 126, 234, 0.02) 100%)',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  sectionTitle: {
    fontSize: '32px',
    fontWeight: '800',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    margin: '0 0 12px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    letterSpacing: '-0.5px',
    flexWrap: 'wrap',
  },
  onlineIndicator: {
    fontSize: '20px',
    animation: 'pulse 2s infinite',
  },
  sectionSubtitle: {
    fontSize: '17px',
    color: '#6b7280',
    margin: 0,
    fontWeight: '500',
  },
  usersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  userCard: {
    backgroundColor: 'white',
    borderRadius: '20px',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.15)',
    position: 'relative',
    cursor: 'default',
  },
  cardContent: {
    padding: '18px',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '14px',
  },
  userInitial: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: '700',
    position: 'relative',
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
    transition: 'all 0.3s ease',
  },
  onlineStatus: {
    position: 'absolute',
    bottom: '-2px',
    right: '-2px',
    width: '14px',
    height: '14px',
    backgroundColor: '#10b981',
    borderRadius: '50%',
    border: '2px solid white',
    boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.2)',
    animation: 'pulse-ring 2s ease-out infinite',
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  cardName: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 6px 0',
    letterSpacing: '-0.2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: '500',
  },
  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  metaDivider: {
    color: '#d1d5db',
    fontWeight: '400',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
  },
  connectBtn: {
    flex: 1,
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
    letterSpacing: '0.2px',
  },
  connectBtnDisabled: {
    backgroundColor: '#d1d5db',
    cursor: 'not-allowed',
    background: '#d1d5db',
    boxShadow: '0 2px 6px rgba(156, 163, 175, 0.15)',
    opacity: 0.7,
  },
  chatBtn: {
    flex: 1,
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    letterSpacing: '0.2px',
  },
  pendingBtn: {
    flex: 1,
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    boxShadow: '0 3px 10px rgba(239, 68, 68, 0.25)',
    letterSpacing: '0.2px',
    transition: 'all 0.3s ease',
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 40px',
    color: '#6b7280',
  },
  emptyIcon: {
    fontSize: '96px',
    marginBottom: '24px',
    display: 'inline-block',
    animation: 'float 3s ease-in-out infinite',
    filter: 'drop-shadow(0 8px 16px rgba(102, 126, 234, 0.15))',
  },
  emptyTitle: {
    fontSize: '28px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    marginBottom: '12px',
  },
  emptyText: {
    fontSize: '17px',
    lineHeight: '1.7',
    color: '#6b7280',
    maxWidth: '500px',
    margin: '0 auto',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f4f6',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  loadingText: {
    color: '#374151',
    margin: 0,
  },
};

// Add CSS animations
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  
  @keyframes pulse-ring {
    0% {
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
    }
  }
  
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
  }
  
  [style*="userCard"]:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(102, 126, 234, 0.25), 0 0 0 1px rgba(102, 126, 234, 0.15), inset 0 1px 0 rgba(255,255,255,0.8) !important;
    filter: brightness(1.02);
  }
  
  [style*="userCard"]::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    opacity: 0.6;
  }
  
  [style*="userInitial"]:hover {
    transform: scale(1.08) rotate(-5deg);
  }
  
  [style*="connectBtn"]:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4) !important;
  }
  
  [style*="chatBtn"]:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4) !important;
  }
  
  [style*="connectBtn"]:not(:disabled):active,
  [style*="chatBtn"]:active {
    transform: translateY(0px) scale(0.96);
  }
  
  [style*="acceptBtn"]:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
  }
  
  [style*="logoutBtn"]:hover {
    background-color: rgba(255,255,255,0.3);
  }
`;
document.head.appendChild(styleSheet);

export default OnlineUsers;
